import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import DialogContent from '@mui/material/DialogContent'
import Dialog from '@mui/material/Dialog'
import { path, prop, propOr } from 'ramda'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Checkbox from '@mui/material/Checkbox/Checkbox'
import DialogActions from '@mui/material/DialogActions/DialogActions'
import InfiniteScroll from 'react-infinite-scroll-component'
import { styled } from '@mui/material'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useField } from 'react-final-form'
import TableHead from '@mui/material/TableHead'
import { useTranslation } from 'react-i18next'

import DialogTitle from '~/components/DialogTitle'
import TextOverflow from '~/components/TextOverflow'
import Avatar from '~/components/Avatar'
import { Button, DiscardButton } from '~/components/Buttons'
import CurrencyMoney from '~/components/Money/CurrencyMoney'
import Status, { ERROR, SUCCESS } from '~/components/Status'
import InfinityScrollSearch from '~/components/InfinityScrollSearch'

import VariantTableSkeletonList from './VariantTableSkeletonList'

const TableRowStyled = styled(TableRow)({
  cursor: 'pointer',
  '&:hover': {
    background: '#0000000A'
  }
})

const DialogContentStyled = styled(DialogContent)({
  padding: '0',
  position: 'relative',
  height: 'calc(100vh / 2)'
})

function TransferVariantAddDialog ({ open, onClose, onAddVariants, params, transferOrderVariant }) {
  const { t } = useTranslation()

  const lineItemsField = useField('lineItems')
  const lineItemsFieldValue = lineItemsField.input.value || []

  const [selected, setSelected] = useState(lineItemsFieldValue)

  const results = propOr([], 'results', transferOrderVariant)
  const count = prop('count', transferOrderVariant)

  const searchField = useField('search')
  const searchFieldValue = searchField.input.value

  const onEscape = useCallback(event => {
    if (event.code === 'Escape') {
      onClose()
    }
  }, [onClose])

  const handleCheck = (checked, variant) => {
    const id = prop('id', variant)
    const newVariant = { ...variant, variant: { id } }
    if (checked) {
      setSelected(prevSelected => prevSelected.filter(select => path(['variant', 'id'], select) !== id))
    } else {
      setSelected(prevSelected => [...prevSelected, newVariant])
    }
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth={true} onKeyDown={onEscape}>
      <DialogTitle variant="h6">
        {`${t('Browse Variants')} - ${t('Selected')} (${selected.length})`}
        <Box sx={{ padding: '24px 0' }}>
          <InfinityScrollSearch
            onEffect={transferOrderVariant.reset}
            getList={transferOrderVariant.getList}
            params={params}
          />
        </Box>
      </DialogTitle>
      <DialogContentStyled id="transferVariantTable">
        <InfiniteScroll
          dataLength={results.length}
          hasMore={count > results.length}
          loader=""
          next={() => transferOrderVariant.getList({ search: searchFieldValue, ...params })}
          scrollableTarget="transferVariantTable"
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={70}>{t('Select')}</TableCell>
                  <TableCell width={70}>{t('Image')}</TableCell>
                  <TableCell width={380}>{t('Variant')}</TableCell>
                  <TableCell width={290}>{t('Price')}</TableCell>
                  <TableCell width={90}>{t('Available')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((variant, index) => {
                  const id = prop('id', variant)
                  const src = prop('defaultImage', variant)
                  const checked = selected.find(select => path(['variant', 'id'], select) === id)
                  const available = prop('available', variant)
                  const statusColor = available > 0 ? SUCCESS : ERROR
                  return (
                    <TableRowStyled key={id} onClick={() => handleCheck(checked, variant)}>
                      <TableCell data-cy={`variant-${index}`}>
                        <Checkbox checked={Boolean(checked)} color="secondary" />
                      </TableCell>
                      <TableCell>
                        <Avatar
                          alt={prop('name', variant)}
                          src={src}
                          variant="rounded"
                        />
                      </TableCell>
                      <TableCell>
                        <TextOverflow
                          lines={1}
                          selfTooltip={true}
                        >
                          {prop('name', variant)}
                        </TextOverflow>
                      </TableCell>
                      <TableCell>
                        <CurrencyMoney value={prop('retailPrice', variant)} />
                      </TableCell>

                      <TableCell>
                        <TextOverflow tooltip="Available quantity" >
                          <Typography variant="body2">
                            <Status label={available} color={statusColor} />
                          </Typography>
                        </TextOverflow>
                      </TableCell>
                    </TableRowStyled>
                  )
                })}
                {transferOrderVariant.isLoading && (
                  <VariantTableSkeletonList />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      </DialogContentStyled>
      <DialogActions>
        <DiscardButton
          onClick={onClose}
          fullWidth={true}
        >
          Discard
        </DiscardButton>
        <Button
          data-cy="add"
          variant="contained"
          fullWidth={true}
          onClick={() => onAddVariants(selected)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

TransferVariantAddDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddVariants: PropTypes.func.isRequired,
  params: PropTypes.object,
  transferOrderVariant: PropTypes.object.isRequired
}

export default TransferVariantAddDialog
